import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';

import { Formatters, Messages } from '../../../../../gen-src/Localize';
import { DatabaseTableOrderField } from '../../../../../gen-src/queries';
import {
  ColumnFactory,
  createIconCellFormatter,
  createLabelColumnFormatter,
  createSelectionColumn,
  createSortableHeaderFormatter,
  createTextCellFormatter,
  ExplorerStyledDisplayGrid,
  GraphLabel,
  GraphQLError,
  GridSortProps,
  LabelContent,
  reportGraphQLErrors,
  SelectionProviderChildProps,
  SiteUrlsContext,
  TypedColumn,
  UnsortableColumnKey,
  UseRenderLabelsParams,
  UsualDisplayGridProps,
} from '../../../data-catalog-shared';
import { getLabelParamsForGrids, labelCategoryToString, LabelGridWrapperProps } from '../Labels/LabelsHookHelper';
import {
  createQueryPreservingReplacingLinkCellFormatter,
} from '../datagrid/cellFormatters/QueryPreservingLinkCellFormatter';
import { VirtualConnectionTableTypeIcon } from '../datagrid/cellFormatters/VirtualConnectionTableTypeIcon';
import { GlobalAssetGridDC3ColumnFactoryParams } from '../sorting/makeColumnFactoryParams';
import { TablesValidSortFields } from '../sorting/sortProviders/TablesGridSortProvider';
import { VirtualConnectionTableTypeMappings } from '../table/functions/VirtualConnectionTableTypeMappings';
import { VirtualConnectionTablesDisplayGridRowData } from '../table/types/VirtualConnectionTablesDisplayGridRowData';

type VirtualConnectionTablesColumnFactory = ColumnFactory<
  GlobalAssetGridDC3ColumnFactoryParams<TablesValidSortFields>,
  VirtualConnectionTablesDisplayGridRowData,
  TablesValidSortFields
>;

function columns(siteUrls: SiteUrls): ReadonlyArray<VirtualConnectionTablesColumnFactory> {
  const columns: ReadonlyArray<VirtualConnectionTablesColumnFactory> = [
    () => createSelectionColumn(),
    (sortParams) => ({
      name: 'type',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderType(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createIconCellFormatter(
        ({ isExtracted }) => <VirtualConnectionTableTypeIcon
          isExtracted={!!isExtracted}
        />,
        ({ isExtracted }) => VirtualConnectionTableTypeMappings({
          isExtracted: !!isExtracted,
        }),
      ),
      sortable: false,
      resizable: false
    }),
    () => createLabelGridColumnFormatter(siteUrls, 'CERTIFICATION'),
    () => createLabelGridColumnFormatter(siteUrls, 'WARNING'),
    (sortParams) => ({
      name: 'name',
      dataKey: DatabaseTableOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createQueryPreservingReplacingLinkCellFormatter(
        ({ name }) => name || '',
        (urls, { vizportalUrlId }) => urls.virtualConnectionTableLineagePath(vizportalUrlId),
      ),
      sortable: true,
    }),
    (sortParams) => ({
      name: 'workbooks',
      dataKey: DatabaseTableOrderField.DOWNSTREAM_WORKBOOKS_COUNT,
      headerText: Messages.GridHeaderWorkbooks(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTextCellFormatter(
        ({ downstreamWorkbooksConnection }) =>
          downstreamWorkbooksConnection
            ? Formatters.formatCount(downstreamWorkbooksConnection.totalCount)
            : null
      ),
      sortable: true,
    }),
    (sortParams) => ({
      name: 'datasources',
      dataKey: DatabaseTableOrderField.DOWNSTREAM_DATASOURCES_COUNT,
      headerText: Messages.GridHeaderDatasources(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTextCellFormatter(
        ({ downstreamDatasourcesConnection }) =>
          downstreamDatasourcesConnection
            ? Formatters.formatCount(downstreamDatasourcesConnection.totalCount)
            : null
      ),
      sortable: true,
    }),
    (sortParams) => ({
      name: 'extractStatus',
      dataKey: UnsortableColumnKey,
      cellFormatter: createTextCellFormatter(
        ({ isExtracted }) =>
          isExtracted
            ? Messages.ExtractConnection()
            : Messages.LiveConnection()
      ),
      headerText: Messages.GridHeaderConnectionStatus(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      sortable: false,
    }),
  ];

  return columns.filter(Boolean) as ReadonlyArray<VirtualConnectionTablesColumnFactory>;
}

export type VirtualConnectionTablesDisplayGridProps =
  UsualDisplayGridProps<VirtualConnectionTablesDisplayGridRowData, GraphQLError> &
  SelectionProviderChildProps &
  GridSortProps<TablesValidSortFields>;

export function VirtualConnectionTablesDisplayGrid(props: VirtualConnectionTablesDisplayGridProps): JSX.Element {
  const siteUrls = React.useContext(SiteUrlsContext);
  const cols = columns(siteUrls).map(columnFactory => columnFactory(props));

  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      selectedKeys={props.selectedKeys}
      updateSelectionForKey={props.updateSelectionForKey}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}

function getLabelContentFromRowData(rowData: VirtualConnectionTablesDisplayGridRowData, urls: SiteUrls) {
  return {
    contentId: rowData.id,
    contentLuid: rowData.luid,
    contentType: rowData.contentType,
    contentName: rowData.name ?? undefined,
    contentUrl: urls.virtualConnectionTableLineageUrl(rowData.vizportalUrlId),
    subjectArea: rowData.__typename
  };
}

function createLabelGridColumnFormatter(urls: SiteUrls, category: LabelGridWrapperProps['category']): TypedColumn<VirtualConnectionTablesDisplayGridRowData, TablesValidSortFields> {

  function getPropsFromRowData(rowData: VirtualConnectionTablesDisplayGridRowData): UseRenderLabelsParams {
    const content: LabelContent = getLabelContentFromRowData(rowData, urls);
    const labels: ReadonlyArray<GraphLabel> = rowData.labels ? rowData.labels : [];
    return getLabelParamsForGrids({ category, labels, urls, source: 'virtualconnections', content });
  }

  return createLabelColumnFormatter(labelCategoryToString(category), { overrides: {}, getPropsFromRowData }) as
    TypedColumn<VirtualConnectionTablesDisplayGridRowData, TablesValidSortFields>;
}
